import {useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';

import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp, utilServerSideDeviceDetection} from '@utils';
import {metaInfo} from '@configs';
import {EPOCH, EPOCH_REGISTER, HOME, ALIAS_TO_COMPETITION_ID} from '@constants';

import loadSEO from '@helpers/loadSEO';
import dynamic from 'next/dynamic';
import Loader from '@jobseeker/lotus/components/Loader';
import cookie from 'cookie';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {competitionService, userService} from '@jobseeker/common-core/services';
import {getCookiesToDelete} from '@helpers/ssrMap';
import {isCSR} from '@jobseeker/lotus/utils';
import {SeoIntrup} from '@layouts/SeoLayout';
import {CompetitionBanner} from '@components/Competition';
import {getFormatedProfile} from '@jobseeker/common-core/utils/dataFormation';
import {Box, Link, Typography} from '@mui/material';

import type {GetServerSidePropsContext} from 'next/types';
import type {AppState, UserState} from '@jobseeker/common-core/types';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {useEffect, useState} from 'react';
import moment from 'moment';
import {useWrappedTheme} from '@jobseeker/lotus/hooks/common';
import {colorPalette} from '@jobseeker/lotus/foundations/colors';

const RegisterHarness = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/CompetitionHarness/RegisterHarness'
    ),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

const LoginForm = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/AuthenticationHarness/AuthForm'
    ),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

type RegisterProps = {
  seoConfig: object;
  loggedRegister: boolean;
  alreadyRegistered: boolean;
  metaData: object;
};

const Register = ({
  seoConfig,
  loggedRegister,
  alreadyRegistered,
  metaData,
}: RegisterProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  const enableLogin = useSelector(
    (state: AppState) => state.app.layout.enableLoginPopup
  );
  useValidateAuth(dispatch);

  const [allowed, setAllowed] = useState<null | boolean>(null);

  const {
    timelines: {
      registration: {startDate, endDate},
      config: {competitionName},
    },
  } = useSelector((state: AppState) => state.competition);

  useEffect(() => {
    if (startDate && endDate) {
      const check = moment().isBetween(moment(startDate), moment(endDate));
      if (check) {
        setAllowed(true);
      } else {
        setAllowed(false);
      }
    }
  }, [startDate, endDate]);

  const scrollToId = (id: string) => {
    const offSet = document?.getElementById(id)?.offsetTop;
    if (offSet) {
      window?.scrollTo({behavior: 'smooth', top: offSet - 70});
    }
  };

  useEffect(() => {
    if (!isCSR() || !allowed) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20; // ~3s total with 150ms interval

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById('RegistrationStarts');
      if (el) {
        scrollToId('RegistrationStarts');
      } else if (attempts++ < maxAttempts) {
        setTimeout(tryScroll, 150);
      }
    };

    tryScroll();

    return () => {
      cancelled = true;
    };
  }, [allowed]);

  const theme = useWrappedTheme();

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout page={EPOCH}>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={[]}
          >
            {isCSR() && (
              <SeoIntrup
                pageNo={0}
                nextPage={false}
                type={competitionName as 'epoch' | 'x-clause'}
              />
            )}
            <CompetitionBanner />
            <Box mt={{xxs: 3, md: 10}}></Box>
            {allowed && (
              <RegisterHarness
                loggedRegister={loggedRegister}
                alreadyRegistered={alreadyRegistered}
              />
            )}
            {allowed !== null && !allowed && (
              <Box textAlign={'center'}>
                <Typography
                  fontSize={{
                    xxs: theme.typography.pxToRem(22),
                    md: theme.typography.pxToRem(30),
                  }}
                  fontWeight={theme.typography.fontWeightBold}
                  color={colorPalette.black['A300']}
                >
                  Registration is now closed!!!
                </Typography>
                <Typography variant="body2" color={'text.secondary'}>
                  Please visit{' '}
                  <Link
                    href={`/${competitionName}`}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                  >
                    {competitionName.toUpperCase()}
                  </Link>{' '}
                  for other details. You can also checkout{' '}
                  <Link
                    href="/"
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                  >
                    {process.env.NEXT_PUBLIC_APP_DOMAIN}
                  </Link>{' '}
                  for job opportunities.
                </Typography>
              </Box>
            )}
            <Box mb={{xxs: 8.5, md: 16, xl: 9.5}}></Box>
            {enableLogin && (
              <Dialog
                title="You're already registered with us! 👋"
                subtitle={`Please login and then register for ${competitionName}.`}
                invertedTitle
                variant={VersionVariants.VERSION_TWO}
                open={enableLogin}
                onClose={() =>
                  dispatch({
                    type: 'AppContext/set_login_popup',
                    payload: false,
                  })
                }
              >
                <LoginForm
                  onSuccess={() => {
                    window.location.reload();
                  }}
                  variant={VersionVariants.VERSION_TWO}
                  signUpLink={`/${competitionName}/register`}
                />
              </Dialog>
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const metaData = getConfigByApp(metaInfo, EPOCH, true, EPOCH_REGISTER);

    /**
     * Mandatory for unique routes agreement
     * Only allow the application routes to access the page
     */
    const {
      routesAllowed,
    }: /* eslint-disable @typescript-eslint/no-explicit-any */ any =
      metaData || {};

    if (!routesAllowed?.includes(EPOCH)) {
      return {
        notFound: true,
      };
    }

    if (!metaData.featureFlags.competition) {
      return {
        notFound: true,
      };
    }
    const alias = (context.query.alias as string) || 'epoch';

    const competitionId = ALIAS_TO_COMPETITION_ID[alias];

    const timelineInfo = await competitionService.fetchTimelineData(
      competitionId
    );

    if (
      timelineInfo?.registration &&
      Object.keys(timelineInfo?.registration).length > 0
    ) {
      store.dispatch({
        type: 'Competition/set_timeline_data',
        payload: {competition: timelineInfo},
      });
    }

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: EPOCH_REGISTER},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const seoConfig = await loadSEO(HOME);
    const loggedRegister = false;
    let alreadyRegistered = false;

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);
        console.log(
          '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
          context.req?.headers
        );

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const headers = {
            cookie: context.req?.headers?.cookie,
          };

          try {
            const {profile} = await userService.profile(
              `profile?epoch=true&competitionId=${competitionId}`,
              headers
            );

            if (profile) {
              const formatedProfile: UserState = getFormatedProfile(profile);

              store.dispatch({
                type: 'User/set_user_profile',
                payload: formatedProfile.profile,
              });

              // const {
              //   completeness: {profileStatus},
              //   profile: {resumeUpdated},
              // } = formatedProfile;
              // if (
              //   profileStatus?.personalDetails &&
              //   profileStatus?.educationDetails &&
              //   profileStatus?.resumeAttached &&
              //   resumeUpdated
              // ) {
              //   loggedRegister = true;
              // }

              if (
                formatedProfile.profile.competitionIdentity &&
                formatedProfile.profile.testSlug
              ) {
                alreadyRegistered = true;
              }
            }
          } catch (error) {
            console.log(`🚀 ~ ${EPOCH_REGISTER}=> ~ error:`, error);
          }
        }
      }
    } catch (error: /* eslint-disable @typescript-eslint/no-explicit-any */ any) {
      console.log('🚀 ~ file: index.tsx:71 ~ store=> ~ validate error:', error);

      const cookiesTodelete = getCookiesToDelete();
      context.res?.setHeader('Set-Cookie', cookiesTodelete);
    }

    return {
      props: {
        seoConfig,
        loggedRegister,
        alreadyRegistered,
        metaData,
      },
    };
  }
);

export default Register;
